import type { Policy, CreatePolicyInput } from '@communities/content-management/domain';
import { WorkflowType } from '@communities/content-management/domain';
import { useStrikeConfigValidation } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { LoadingSpinner, TextInput, TextArea, Dialog, Switch } from '@soteria/design-system-ve';
import { useState, useEffect, useCallback, useMemo } from 'react';

import * as styles from './StrikePolicyDialog.css';

/**
 * Internal form data for the strike policy dialog.
 * Tracks all form fields independently from the API input type.
 */
interface StrikePolicyFormData {
  name: string;
  description: string;
  isEnabled: boolean;
  strikeThreshold: string;
}

interface StrikePolicyDialogProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  communityId: string;
  policy?: Policy;
  existingThresholds: number[];
  errors?: DomainError[] | null;
  onClose: () => void;
  onSave: (input: CreatePolicyInput) => Promise<boolean>;
}

const SESSION_STORAGE_PREFIX = 'strike-policy-draft';

/**
 * Builds the session storage key for persisting draft form data.
 */
function getDraftKey(communityId: string, mode: string): string {
  return `${SESSION_STORAGE_PREFIX}-${communityId}-${mode}`;
}

/**
 * Creates default form data for a new strike policy.
 */
function getDefaultFormData(): StrikePolicyFormData {
  return {
    name: '',
    description: '',
    isEnabled: true,
    strikeThreshold: '',
  };
}

/**
 * Creates form data from an existing policy for edit mode.
 */
function getFormDataFromPolicy(policy: Policy): StrikePolicyFormData {
  return {
    name: policy.name || '',
    description: policy.description || '',
    isEnabled: policy.isEnabled,
    strikeThreshold: policy.strikeThreshold !== null && policy.strikeThreshold !== undefined ? String(policy.strikeThreshold) : '',
  };
}

/**
 * Dialog for creating or editing strike policies.
 * Strike policies define automated actions triggered when users reach strike point thresholds.
 * Includes client-side validation, session storage draft persistence, and error display.
 */

export function StrikePolicyDialog({ isOpen, mode, communityId, policy, existingThresholds, errors, onClose, onSave }: StrikePolicyDialogProps) {
  const [formData, setFormData] = useState<StrikePolicyFormData>(getDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { validateStrikePolicy } = useStrikeConfigValidation();
  const isEditMode = mode === 'edit';

  const draftKey = useMemo(() => getDraftKey(communityId, mode), [communityId, mode]);

  // Reset form when dialog opens or policy changes
  useEffect(() => {
    if (isOpen) {
      const draft = sessionStorage.getItem(draftKey);

      if (draft) {
        try {
          const parsed = JSON.parse(draft) as StrikePolicyFormData;

          setFormData(parsed);
        } catch {
          // Corrupted draft - use default or policy data
          setFormData(isEditMode && policy ? getFormDataFromPolicy(policy) : getDefaultFormData());
        }
      } else {
        setFormData(isEditMode && policy ? getFormDataFromPolicy(policy) : getDefaultFormData());
      }

      setValidationError(null);
    }
  }, [isOpen, policy, isEditMode, draftKey]);

  // Persist draft to session storage on form changes
  const persistDraft = useCallback(
    (data: StrikePolicyFormData) => {
      sessionStorage.setItem(draftKey, JSON.stringify(data));
    },
    [draftKey]
  );

  const handleFieldChange = useCallback(
    (field: keyof StrikePolicyFormData, value: string | boolean) => {
      setFormData(prev => {
        const updated = { ...prev, [field]: value };

        persistDraft(updated);

        return updated;
      });

      // Clear validation error when user makes changes
      setValidationError(null);
    },
    [persistDraft]
  );

  const handleThresholdChange = useCallback(
    (value: string) => {
      // Allow only positive integers (strip non-numeric characters)
      const cleaned = value.replace(/[^0-9]/g, '');

      handleFieldChange('strikeThreshold', cleaned);
    },
    [handleFieldChange]
  );

  /**
   * Validates the form and returns true if valid.
   * Uses useStrikeConfigValidation for threshold checks
   * and performs name-required validation locally.
   */
  const validateForm = useCallback((): boolean => {
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      setValidationError('Policy name is required');

      return false;
    }

    const threshold = parseInt(formData.strikeThreshold, 10);

    if (isNaN(threshold) || threshold <= 0) {
      setValidationError('Strike threshold must be a positive integer');

      return false;
    }

    // Filter out the current policy's threshold when editing to allow keeping the same value
    const thresholdsToCheck =
      isEditMode && policy?.strikeThreshold !== null && policy?.strikeThreshold !== undefined
        ? existingThresholds.filter(t => t !== policy.strikeThreshold)
        : existingThresholds;

    // validateStrikePolicy expects actions array - pass a non-empty placeholder
    // since strike policy actions are managed separately via the ActionsTab
    const policyError = validateStrikePolicy(threshold, ['placeholder'], thresholdsToCheck);

    if (policyError) {
      setValidationError(policyError);

      return false;
    }

    return true;
  }, [formData, isEditMode, policy, existingThresholds, validateStrikePolicy]);

  const isFormValid = useMemo(() => {
    const trimmedName = formData.name.trim();
    const threshold = parseInt(formData.strikeThreshold, 10);

    return trimmedName.length > 0 && !isNaN(threshold) && threshold > 0;
  }, [formData.name, formData.strikeThreshold]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const input: CreatePolicyInput = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      isEnabled: formData.isEnabled,
      workflowType: WorkflowType.STRIKE,
      labels: [],
      strikeThreshold: parseInt(formData.strikeThreshold, 10),
    };

    try {
      const success = await onSave(input);

      if (success) {
        sessionStorage.removeItem(draftKey);
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onSave, draftKey, onClose]);

  const handleClose = useCallback(() => {
    setValidationError(null);
    onClose();
  }, [onClose]);

  const dialogTitle = isEditMode ? 'Edit Strike Policy' : 'Create Strike Policy';
  const dialogDescription = isEditMode ? 'Update strike policy settings and threshold.' : 'Configure a new strike policy with a point threshold.';
  const submitLabel = isEditMode ? 'Update Policy' : 'Create Policy';

  return (
    <Dialog
      description={dialogDescription}
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : submitLabel,
        onClick: handleSubmit,
        disabled: isSubmitting || !isFormValid,
      }}
      size="xl"
      title={dialogTitle}
      onClose={handleClose}
    >
      <div className={styles.formContainer}>
        {/* API Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the policy:" />}

        {/* Client-side Validation Error */}
        {validationError && (
          <div aria-live="polite" className={styles.validationError} role="alert">
            {validationError}
          </div>
        )}

        {/* Policy Name */}
        <TextInput
          required
          id="strikePolicyName"
          label="Policy Name"
          placeholder="Enter policy name"
          value={formData.name}
          onChange={value => handleFieldChange('name', value)}
        />

        {/* Description */}
        <TextArea
          id="strikePolicyDescription"
          placeholder="Enter policy description"
          title="Description"
          value={formData.description}
          onChange={e => handleFieldChange('description', e.target.value)}
        />

        {/* Strike Threshold */}
        <div className={styles.fieldGroup}>
          <TextInput
            required
            id="strikeThreshold"
            label="Strike Threshold (Points)"
            placeholder="Enter point threshold (e.g., 10)"
            type="number"
            value={formData.strikeThreshold}
            onChange={handleThresholdChange}
          />
        </div>

        {/* Enabled Switch */}
        <Switch checked={formData.isEnabled} label="Policy Enabled" onChange={checked => handleFieldChange('isEnabled', checked)} />
      </div>
    </Dialog>
  );
}
